import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { IApp } from "@/types";
import { makeAlert } from "@/utils";
import Profile from "@/actions/Profile";

export const useProfileStore = defineStore("store", () => {
  const profile = ref<IApp.IProfile | null>(null);
  const prompts = ref<Array<IApp.IPrompt>>([]);
  const defaultPrompt = ref<string>("");
  const profileLoadingStatus = ref<IApp.ILoadingStatus>(IApp.ILoadingStatus.Idle);
  const isLoading = computed(() => profileLoadingStatus.value == IApp.ILoadingStatus.Loading);

  const fetch = async () => {
    profileLoadingStatus.value = IApp.ILoadingStatus.Loading;
    const response = await Profile.get();
    if (response.status) {
      profile.value = response.data.profile;
      prompts.value = response.data.prompts;
      defaultPrompt.value = response.data.defaultPrompt;
      profileLoadingStatus.value = IApp.ILoadingStatus.Success;
    } else {
      profileLoadingStatus.value = IApp.ILoadingStatus.Error;
      makeAlert(response.message, "error");
    }
  };

  const update = async (data: Partial<IApp.IProfile>) => {
    profileLoadingStatus.value = IApp.ILoadingStatus.Loading;

    const response = await Profile.update(data);

    if (response.status) {
      profile.value = response.data.profile;
      prompts.value = response.data.prompts;
      defaultPrompt.value = response.data.defaultPrompt;
      profileLoadingStatus.value = IApp.ILoadingStatus.Success;
    } else {
      profileLoadingStatus.value = IApp.ILoadingStatus.Error;
      makeAlert(response.message, "error");
    }
  };

  const createPrompt = async (prompt: Omit<IApp.IPrompt, "id">) => {
    profileLoadingStatus.value = IApp.ILoadingStatus.Loading;
    const response = await Profile.createPrompt(prompt);
    if (response.status) {
      prompts.value.push(response.data.prompt);
      profileLoadingStatus.value = IApp.ILoadingStatus.Success;
    } else {
      profileLoadingStatus.value = IApp.ILoadingStatus.Error;
      makeAlert(response.message, "error");
    }
  };

  const updatePrompt = async (prompt: IApp.IPrompt) => {
    profileLoadingStatus.value = IApp.ILoadingStatus.Loading;
    const response = await Profile.updatePrompt(prompt);
    if (response.status) {
      const index = prompts.value.findIndex((p) => p.id === prompt.id);
      if (index !== -1) {
        prompts.value[index] = prompt;
      } else {
        prompts.value.push(prompt);
      }
      await fetch();
      profileLoadingStatus.value = IApp.ILoadingStatus.Success;
      makeAlert("Prompt updated successfully", "success");
    } else {
      profileLoadingStatus.value = IApp.ILoadingStatus.Error;
      makeAlert(response.message, "error");
    }
  };

  const deletePrompt = async (id: string) => {
    prompts.value = prompts.value.filter((p) => p.id !== id);
  };

  const setDefaultPrompt = async (id: string | null) => {
    profileLoadingStatus.value = IApp.ILoadingStatus.Loading;
    const response = await Profile.update({
      userId: profile.value?.userId,
      defaultPromptId: id
    });
    if (response.status) {
      profile.value = response.data.profile;
      prompts.value = response.data.prompts;
      defaultPrompt.value = response.data.defaultPrompt;
      profileLoadingStatus.value = IApp.ILoadingStatus.Success;
      makeAlert("Default prompt updated successfully", "success");
    } else {
      profileLoadingStatus.value = IApp.ILoadingStatus.Error;
      makeAlert(response.message, "error");
    }
  };

  return {
    fetch,
    update,
    createPrompt,
    updatePrompt,
    deletePrompt,
    setDefaultPrompt,
    isLoading,
    profile,
    prompts,
    defaultPrompt,
    profileLoadingStatus
  };
});
